import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { fi } from './locales/fi';

function aliasSingularPlural(ns: Record<string, unknown>, singular: string, plural: string) {
  const s = ns[singular] as Record<string, unknown> | undefined;
  const p = ns[plural] as Record<string, unknown> | undefined;
  if (!s || !p) return;
  ns[singular] = { ...p, ...s };
  ns[plural] = { ...s, ...p };
}

const SINGULAR_PLURAL_PAIRS = [
  ['apartment', 'apartments'],
  ['key', 'keys'],
  ['resident', 'residents'],
  ['condominium', 'condominiums'],
  ['user', 'users'],
] as const;

function applySingularPluralAliases(translation: Record<string, unknown>) {
  for (const [singular, plural] of SINGULAR_PLURAL_PAIRS) {
    aliasSingularPlural(translation, singular, plural);
  }
}

applySingularPluralAliases(fi.translation as unknown as Record<string, unknown>);

const supportedLngs = ['fi', 'en', 'sv'] as const;
type SupportedLng = (typeof supportedLngs)[number];

function normalizeLng(lng: string | undefined): SupportedLng {
  const code = (lng ?? 'fi').split('-')[0]?.toLowerCase() ?? 'fi';
  if (code === 'en' || code === 'sv' || code === 'fi') return code;
  return 'fi';
}

const languageLoaders: Partial<Record<SupportedLng, Promise<void>>> = {};

/** Load a non-Finnish locale bundle on demand (keeps login cold path on fi only). */
export async function ensureLanguage(lng: string | undefined): Promise<void> {
  const code = normalizeLng(lng);
  if (code === 'fi') return;
  if (i18n.hasResourceBundle(code, 'translation')) return;

  const existing = languageLoaders[code];
  if (existing) {
    await existing;
    return;
  }

  const load = (async () => {
    if (code === 'en') {
      const { en } = await import('./locales/en');
      applySingularPluralAliases(en.translation as unknown as Record<string, unknown>);
      i18n.addResourceBundle('en', 'translation', en.translation, true, true);
      return;
    }
    const { sv } = await import('./locales/sv');
    applySingularPluralAliases(sv.translation as unknown as Record<string, unknown>);
    i18n.addResourceBundle('sv', 'translation', sv.translation, true, true);
  })();

  languageLoaders[code] = load;
  try {
    await load;
  } catch (error) {
    delete languageLoaders[code];
    throw error;
  }
}

const APP_TITLES: Record<SupportedLng, string> = {
  fi: 'Xkey - Avaintenhallintasovellus',
  en: 'Xkey - Key management',
  sv: 'Xkey - Nyckelhantering',
};

export function getDefaultAppTitle(lng?: string): string {
  return APP_TITLES[normalizeLng(lng ?? i18n.language)];
}

export function syncDocumentLocale(lng: string | undefined): void {
  if (typeof document === 'undefined') return;
  const code = normalizeLng(lng);
  document.documentElement.lang = code;
  const titles = Object.values(APP_TITLES);
  if (!document.title || titles.includes(document.title)) {
    document.title = APP_TITLES[code];
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fi: {
        translation: fi.translation,
      },
    },
    partialBundledLanguages: true,
    fallbackLng: 'fi',
    supportedLngs: [...supportedLngs],
    debug: import.meta.env.DEV,

    interpolation: {
      escapeValue: false,
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    // Re-render when `applyTranslationOverrides` merges bundles via addResourceBundle
    react: {
      bindI18nStore: 'added removed',
    },
  });

i18n.on('languageChanged', (lng) => {
  void ensureLanguage(lng).then(() => {
    syncDocumentLocale(lng);
  });
});

/** Resolves when the detected locale bundle is loaded so the first paint is not Finnish fallback. */
export const i18nReady = ensureLanguage(i18n.language).then(() => {
  syncDocumentLocale(i18n.language);
});

export default i18n;
